<template>
  <generic-in-app-screen>
    <div class="d-flex flex-column flex-h-stretch">
      <b-container fluid>
        <h1 v-show="title" class="my-5">
          <span class="fas fa-pencil text-primary" /> <b> {{ title }} </b>
        </h1>
      </b-container>

      <b-container fluid class="flex-h-stretch">
        <div
          id="viewport"
          ref="viewport"
          @scroll.passive="handle_scroll"
          class="h-100"
        >
          <div ref="panner" :class="panner_classes">
            <workflow-canvas-brick
              v-for="suid in bricks_undeleted.lists"
              :key="suid"
              :workflow_interface="workflow_interface"
              :value="bricks_undeleted.bricks[suid]"
              :project_id="project_id"
              :options="component_options"
            />
            <workflow-canvas-connector
              v-for="cuid in connectors_undeleted.lists"
              :key="cuid"
              :workflow_interface="workflow_interface"
              :value="connectors_undeleted.connectors[cuid]"
              :options="connector_options"
              :lhs="
                bricks_undeleted.bricks[
                  connectors_undeleted.connectors[cuid].from_brick_uid
                ]
              "
              :rhs="
                bricks_undeleted.bricks[
                  connectors_undeleted.connectors[cuid].to_brick_uid
                ]
              "
            />
            <workflow-canvas-brick-ghost
              v-if="show_brick_ghost"
              :workflow_interface="workflow_interface"
              :options="brick_ghost_options"
            />
            <workflow-canvas-connector-ghost
              v-if="show_connector_ghost"
              :workflow_interface="workflow_interface"
              :options="connector_ghost_options"
            />
            <div
              ref="areaselector"
              v-show="area_selecting"
              class="areaselector bg-lightgrey border border-darkgrey"
            />
          </div>
        </div>
        <div class="position-relative">
          <transition name="tooltip">
            <div
              ref="tooltip"
              v-show="tooltip_visible"
              :class="palette_description_container_class"
            >
              <workflow-palette-description
                :workflow_interface="workflow_interface"
              />
            </div>
          </transition>
        </div>
      </b-container>
      <!--       <b-container
        ref="controlpanel"
        fluid
        class="d-flex"
        style="height: 270px;"
      > -->
      <b-container
        ref="controlpanel"
        fluid
        :style="{ height: elements_size.controlpanel_height + 'px' }"
        class="d-flex"
      >
        <workflow-minimap
          ref="minimap"
          :workflow_interface="workflow_interface"
          :options="minimap_options"
          :canvas_size="canvas_size"
        />
        <workflow-info-panel
          ref="info_panel"
          :workflow_interface="workflow_interface"
          :options="info_panel_options"
          class="flex-w-stretch"
        />
        <workflow-palette
          ref="palette"
          :workflow_interface="workflow_interface"
          :options="palette_options"
        />
      </b-container>
    </div>
  </generic-in-app-screen>
</template>

<!-- Drawing a grid using CSS
https://stackoverflow.com/questions/4191260/drawing-a-grid-using-css
 -->

<style lang="scss" scoped>
$workflow_grid_step: 16px;
$workflow_brick_size: $workflow_grid_step * 5;
$workflow_brick_icon_size: $workflow_grid_step * 3;

.panner {
  touch-action: none;
}

.grid {
  background-image: repeating-linear-gradient(
      0deg,
      transparent,
      transparent ($workflow_brick_size - 1),
      #ccc ($workflow_brick_size - 1),
      #ccc $workflow_brick_size
    ),
    repeating-linear-gradient(
      -90deg,
      transparent,
      transparent ($workflow_brick_size - 1),
      #ccc ($workflow_brick_size - 1),
      #ccc $workflow_brick_size
    ),
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent ($workflow_grid_step - 1),
      #eee ($workflow_grid_step - 1),
      #eee $workflow_grid_step
    ),
    repeating-linear-gradient(
      -90deg,
      transparent,
      transparent ($workflow_grid_step - 1),
      #eee ($workflow_grid_step - 1),
      #eee $workflow_grid_step
    );
  background-size: $workflow_brick_size $workflow_brick_size;
}

.areaselector {
  opacity: 0.5;
  position: absolute;
}

.tooltip {
  opacity: 0.8;
  position: absolute;
  width: 480px;
  bottom: 0px;
  right: 0px;
  pointer-events: none;
  user-select: none;
}
.tooltip-enter-active {
  transition: none;
}
.tooltip-leave-active {
  transition: all 1s ease;
}
.tooltip-enter,
.tooltip-leave-to {
  opacity: 0;
}
</style>

<i18n>
{
  "en": {
    "project_x_workflow": "{project_name} project workflow",
    "project_x": "{project_name} project"
  },
  "fr": {
    "project_x_workflow": "Workflow du projet {project_name}",
    "project_x": "Projet {project_name}"
  }
}
</i18n>

<script>
import $ from 'jquery'
import _ from 'lodash'
import interact from 'interactjs'
import Mousetrap from 'mousetrap'
import Flatten from 'flatten-js'

import Vue from 'vue'
import { mapState, mapGetters } from 'vuex'

// *****************************************************************************

let point = Flatten.point
let segment = Flatten.segment

import { subscription_mixin, brick_mixin } from '@/components/mixin.js'
import {
  definition_from_name,
  brick_creator_from_name,
  workflow_nb_bricks_in_palette,
  polygon_from_box,
  brick_port_endpoint_positions,
  scale_from_scale_index,
  brick_port_box,
} from './workflow_common.js'
import {
  workflow_brick_size,
  workflow_grid_step,
  workflow_brick_icon_size,
  enclosing_large_box,
  constrain_translate_box,
} from '@/utils.js'
import WorkflowCanvasBrick from '@/components/workflow_canvas_brick.vue'
import WorkflowCanvasConnector from '@/components/workflow_canvas_connector.vue'
import WorkflowCanvasBrickGhost from '@/components/workflow_canvas_brick_ghost.vue'
import WorkflowCanvasConnectorGhost from '@/components/workflow_canvas_connector_ghost.vue'
import WorkflowMinimap from '@/components/workflow_minimap.vue'
import WorkflowInfoPanel from '@/components/workflow_info_panel.vue'
import WorkflowPalette from '@/components/workflow_palette.vue'
import WorkflowPaletteDescription from '@/components/workflow_palette_description.vue'
import GenericInAppScreen from '@/components/generic_in_app_screen.vue'

import {
  generic_create,
  generic_multiupdate,
  generic_delete,
} from '@/api/subscriptions.js'

// function prevent_unallowed_workflow_modifications(wrapped, component) {
//   return function wrapper(...args) {
//     let res
//     if (!component.changes_disabled) {
//       res = wrapped(...args)
//     }
//     return res
//   }
// }
export class WorkflowComponentHandlerBase {
  constructor(component) {
    this.component = component
    this.started = false
  }
  // prevent_unallowed_workflow_modifications(wrapped) {
  //   return prevent_unallowed_workflow_modifications(wrapped, this.component)
  // }
  set_main_handler(main_handler) {
    this.main_handler = main_handler
  }
  start() {
    if (this.started) {
      return
    }
    this.started = true
    this.do_start()
  }
  do_start() {}
  stop() {
    if (!this.started) {
      return
    }
    this.started = false
    this.do_stop()
  }
  do_stop() {}
}

// This function was created to circumvent a bug in interact.js
// where the position of the dragged element is wrongly determined
// at least for detecting a drop event
// It could be optimized by
// - making sure main_handler.position is never overwritten
// (same for brick_value.position)
// - creating the Flatten.point in the handler
// - maybe find a way to override the signature to prevent the
// default dropped argument proposed by interact.js from being computed
function offset_drop_checker_maker(
  main_handler,
  bricks_collection,
  brick_id,
  connector
) {
  function f(
    dragEvent,
    event,
    dropped,
    interactable,
    dropElement,
    draggable,
    draggableElement
  ) {
    const b = brick_port_box(bricks_collection[brick_id].position, connector)
    const p = new Flatten.Point(
      main_handler.drop_position.x,
      main_handler.drop_position.y
    ).box
    const mydropped = b.intersect(p)
    return mydropped
  }
  return f
}

export class WorkflowCanvasBrickHandler extends WorkflowComponentHandlerBase {
  constructor(component) {
    super(component)
    // this.on_drag_start = super.prevent_unallowed_workflow_modifications(
    //   this.on_drag_start.bind(this)
    // )
    // this.on_drag_move = super.prevent_unallowed_workflow_modifications(
    //   this.on_drag_move.bind(this)
    // )
    // this.on_drag_stop = super.prevent_unallowed_workflow_modifications(
    //   this.on_drag_stop.bind(this)
    // )
    // this.on_click = this.on_click.bind(this)

    // this.on_double_click = this.on_double_click.bind(this)

    // this.on_connector_move = super.prevent_unallowed_workflow_modifications(
    //   this.on_connector_move.bind(this)
    // )
    // this.on_connector_stop = super.prevent_unallowed_workflow_modifications(
    //   this.on_connector_stop.bind(this)
    // )
    // this.on_connector_drag_leave = super.prevent_unallowed_workflow_modifications(
    //   this.on_connector_drag_leave.bind(this)
    // )
    this.on_drag_start = this.on_drag_start.bind(this)
    this.on_drag_move = this.on_drag_move.bind(this)
    this.on_drag_stop = this.on_drag_stop.bind(this)

    this.on_click = this.on_click.bind(this)
    this.on_double_click = this.on_double_click.bind(this)

    // this.on_connector_start = this.on_connector_start.bind(this)
    // this.on_connector_move = this.on_connector_move.bind(this)
    // this.on_connector_stop = this.on_connector_stop.bind(this)
    // this.on_connector_drag_enter = this.on_connector_drag_enter.bind(this)
    // this.on_connector_drag_leave = this.on_connector_drag_leave.bind(this)
  }
  do_start() {
    let interactable = interact(this.component.$refs.symbolholder)
    this.interactable = interactable
    interactable
      .draggable({
        autoScroll: {
          container: this.main_handler.viewport,
        },
      })
      .on('dragstart', this.on_drag_start)
      .on('dragmove', this.on_drag_move)
      .on('dragend', this.on_drag_stop)
      .on('tap', this.on_click)
      .on('doubletap', this.on_double_click, true)
      .styleCursor(false)

    let connector_interactables = []
    let defs = definition_from_name(this.component.value.brick_name)
    const connector_ids = 'tl ml bl tr mr br'.split(' ')
    let zcd = _.zip(
      connector_ids,
      _.concat(defs.input.split(''), defs.output.split(''))
    )
    for (const [connector_id, value] of zcd) {
      if (value === ' ') {
        continue
      }
      let name = connector_id + 'arrow'
      const element = this.component.$refs[name]

      let on_connector_start = this.on_connector_start.bind(this, connector_id)
      let on_connector_move = this.on_connector_move.bind(this, connector_id)
      let on_connector_stop = this.on_connector_stop.bind(this, connector_id)
      let on_connector_drag_enter = this.on_connector_drag_enter.bind(
        this,
        connector_id
      )
      let on_connector_drag_leave = this.on_connector_drag_leave.bind(
        this,
        connector_id
      )

      let connector_interactable = interact(element)
      connector_interactable
        .draggable({})
        .dropzone({
          accept: '.synaplus-cosmo-workflow-port',
          checker: offset_drop_checker_maker(
            this.main_handler,
            this.main_handler.component.bricks_undeleted.bricks,
            this.component.value.uid,
            connector_id
          ),
        })
        .on('dragstart', on_connector_start)
        .on('dragmove', on_connector_move)
        .on('dragend', on_connector_stop)
        .on('dragenter', on_connector_drag_enter)
        .on('dragleave', on_connector_drag_leave)
        .styleCursor(false)
      connector_interactables.push([
        connector_interactable,
        on_connector_start,
        on_connector_move,
        on_connector_stop,
        on_connector_drag_enter,
        on_connector_drag_leave,
      ])
    }
    this.connector_interactables = connector_interactables
  }
  do_stop() {
    for (let [
      connector_interactable,
      on_connector_start,
      on_connector_move,
      on_connector_stop,
      on_connector_drag_enter,
      on_connector_drag_leave,
    ] of this.connector_interactables) {
      connector_interactable
        .off('dragleave', on_connector_drag_leave)
        .off('dragenter', on_connector_drag_enter)
        .off('dragend', on_connector_stop)
        .off('dragmove', on_connector_move)
        .off('dragstart', on_connector_start)
        .dropzone(false)
        .draggable(false)
      connector_interactable.unset()
    }
    this.interactable
      .off('doubletap', this.on_double_click)
      .off('tap', this.on_click)
      .off('dragend', this.on_drag_end)
      .off('dragmove', this.on_drag_move)
      .off('dragstart', this.on_drag_start)
      .draggable(false)
    try {
      this.interactable.unset()
    } catch (err) {
      console.warn(err)
    }
  }

  on_double_click() {
    this.main_handler.edit_brick(this.component.value.uid)
  }

  on_click(evt) {
    if (evt.ctrlKey) {
      this.main_handler.add_remove_to_current_selection([
        this.component.value.uid,
      ])
    } else {
      if (
        !this.main_handler.component.current_selection.includes(
          this.component.value.uid
        )
      ) {
        this.main_handler.set_current_selection([this.component.value.uid])
      }
    }
    evt.stopImmediatePropagation()
  }

  on_drag_start(evt) {
    if (
      !this.main_handler.component.current_selection.includes(
        this.component.value.uid
      )
    ) {
      this.main_handler.set_current_selection([this.component.value.uid])
    }

    const bounds = this.main_handler.panner.getBoundingClientRect()
    this.start_mouse_position = {
      x: (evt.clientX - bounds.left) / this.main_handler.scale,
      y: (evt.clientY - bounds.top) / this.main_handler.scale,
    }
    this.start_element_position = this.component.value.position
    this.position = this.start_element_position
  }
  position_from_event(evt) {
    const bounds = this.main_handler.panner.getBoundingClientRect()
    let mouse_position = {
      x: (evt.clientX - bounds.left) / this.main_handler.scale,
      y: (evt.clientY - bounds.top) / this.main_handler.scale,
    }

    // dx, dy = how much the mouse moved
    let dx = mouse_position.x - this.start_mouse_position.x
    let dy = mouse_position.y - this.start_mouse_position.y

    // ndelta = half a grid step
    const ndelta = workflow_grid_step / 2
    // if we move left or right less than ndelta, then we should not
    // change our position
    let position = {
      x:
        this.start_element_position.x +
        Math.floor((dx + ndelta) / workflow_grid_step) * workflow_grid_step,
      y:
        this.start_element_position.y +
        Math.floor((dy + ndelta) / workflow_grid_step) * workflow_grid_step,
    }
    return position
  }

  // TODO: rewrite swapping logic between
  // delta_from_event and position_from_event

  delta_from_event(evt) {
    const old_position = this.position
    const position = this.position_from_event(evt)
    position.x = Math.max(position.x, workflow_brick_size / 2)
    position.x = Math.min(
      position.x,
      $(this.main_handler.panner).width() - workflow_brick_size / 2
    )

    position.y = Math.max(position.y, workflow_brick_size / 2)
    position.y = Math.min(
      position.y,
      $(this.main_handler.panner).height() - workflow_brick_size / 2
    )

    this.position = position
    let delta = {
      x: position.x - old_position.x,
      y: position.y - old_position.y,
    }

    return delta
  }
  move_selection_from_delta(delta) {
    // TODO : Check if that's a correct way to do this
    // and factorize it if yes (used elsewhere)
    const uid_bricks_selected = this.main_handler.component.current_selection.filter(
      uid => this.main_handler.component.bricks_undeleted.lists.includes(uid)
    )

    const all_bricks = this.main_handler.component.bricks_undeleted.bricks

    const bricks_selected = _.reduce(
      uid_bricks_selected,
      function(l, uid) {
        return _.concat(l, all_bricks[uid])
      },
      []
    )

    const canvas = new Flatten.Box(
      0,
      0,
      $(this.main_handler.panner).width(),
      $(this.main_handler.panner).height()
    )

    const initial_selection = enclosing_large_box(bricks_selected)
    const corrected_selection = constrain_translate_box(
      canvas,
      initial_selection
    )

    const final_delta = {
      x: delta.x + corrected_selection.xmin - initial_selection.xmin,
      y: delta.y + corrected_selection.ymin - initial_selection.ymin,
    }

    this.component.$store.commit('move_bricks_from_delta', {
      bricks: uid_bricks_selected,
      delta: final_delta,
    })
  }
  on_drag_move(evt) {
    if (this.component.changes_disabled) {
      return
    }
    let delta = this.delta_from_event(evt)
    this.move_selection_from_delta(delta)
  }
  async on_drag_stop(evt) {
    if (this.component.changes_disabled) {
      return
    }
    let delta = this.delta_from_event(evt)
    this.move_selection_from_delta(delta)

    let self = this
    let prepare_update = function(brick_id) {
      let cur_brick =
        self.main_handler.component.bricks_undeleted.bricks[brick_id]
      let res = {
        uid: brick_id,
        position: cur_brick.position,
      }
      return res
    }

    let bricks_selected = this.main_handler.component.current_selection.filter(
      uid => this.main_handler.component.bricks_undeleted.lists.includes(uid)
    )

    let updates = _.map(bricks_selected, prepare_update)
    await generic_multiupdate(this.main_handler.component.project_id, updates)
  }

  on_connector_start(connector_id) {
    // console.log(this.component.changes_disabled)
    if (this.component.changes_disabled) {
      return
    }
    this.main_handler.change_mode('connecting')
    document.body.style.cursor = 'crosshair'
    this.main_handler.set_drop_source([this.component.value.uid, connector_id])
  }
  on_connector_move(connector_id, evt) {
    const bounds = this.main_handler.panner.getBoundingClientRect()
    this.main_handler.drop_position = {
      x: (evt.clientX - bounds.left) / this.main_handler.scale,
      y: (evt.clientY - bounds.top) / this.main_handler.scale,
    }
  }
  on_connector_stop(connector_id) {
    if (this.main_handler.current_mode !== 'connecting') {
      return
    }
    document.body.style.cursor = 'default'
    this.main_handler.check_for_drop()
    this.main_handler.change_mode('dragging')
  }
  on_connector_drag_enter(connector_id) {
    this.main_handler.set_drop_destination([
      this.component.value.uid,
      connector_id,
    ])
  }
  on_connector_drag_leave(connector_id) {
    this.main_handler.set_drop_destination([-1, -1])
  }
}

export class WorkflowCanvasConnectorHandler extends WorkflowComponentHandlerBase {
  constructor(component) {
    super(component)
    this.on_click = this.on_click.bind(this)
  }
  do_start() {
    this.component.drawer.on_click(this.on_click)
  }
  do_stop() {
    this.component.drawer.on_click(undefined)
  }
  on_click() {
    this.main_handler.set_current_selection([this.component.value.uid])
  }
}

export class WorkflowPaletteHandler extends WorkflowComponentHandlerBase {
  do_start() {
    // this.on_move = this.main_handler.on_mousemove_placing.bind(
    //   this.main_handler
    // )
    let refs = this.component.$refs
    let interactables = []
    for (const i of [...Array(workflow_nb_bricks_in_palette).keys()]) {
      let el = refs['brick' + i][0]
      let interactable = interact(el)
      // let on_dragstart = super.prevent_unallowed_workflow_modifications(
      //   this.on_drag_start.bind(this, i)
      // )
      // let on_dragstop = super.prevent_unallowed_workflow_modifications(
      //   this.on_drag_stop.bind(this, i)
      // )
      let on_tap = this.on_tap.bind(this, i)
      let on_dragstart = this.on_drag_start.bind(this, i)
      let on_dragmove = this.on_drag_move.bind(this, i)
      let on_dragstop = this.on_drag_stop.bind(this, i)
      interactable
        .draggable({})
        .on('dragstart', on_dragstart)
        .on('dragmove', on_dragmove)
        .on('dragend', on_dragstop)
        .on('tap', on_tap)
        .styleCursor(false)
      interactables.push([
        interactable,
        on_tap,
        on_dragstart,
        on_dragmove,
        on_dragstop,
      ])
    }
    this.interactables = interactables
  }
  do_stop() {
    for (let [
      interactable,
      on_tap,
      on_dragstart,
      on_dragmove,
      on_dragstop,
    ] of this.interactables) {
      interactable
        .off('tap', on_tap)
        .off('dragend', on_dragstop)
        .off('dragmove', on_dragmove)
        .off('dragstart', on_dragstart)
        .draggable(false)
    }
    // for (let [interactable, on_tap, on_dragstart, on_move, on_dragstop] of this
    //   .interactables) {
    //   interactable
    //     .off('tap', on_tap)
    //     .off('dragend', on_dragstop)
    //     .off('dragmove', on_move)
    //     .off('dragstart', on_dragstart)
    //     .draggable(false)
    // }
  }

  on_drag_start(i, evt) {
    if (this.component.changes_disabled) {
      return
    }
    const name = this.component.brick_types[i].name

    this.main_handler.move_position = [evt.clientX, evt.clientY]
    this.main_handler.start_drag_and_dropping(name)
    document.body.style.cursor = 'move'
  }
  on_drag_move(i, evt) {
    this.main_handler.on_mousemove_placing(evt)
  }
  on_drag_stop(i, evt) {
    if (this.component.changes_disabled) {
      return
    }
    const name = this.component.brick_types[i].name

    document.body.style.cursor = 'default'
    this.main_handler.change_mode('dragging')
    this.main_handler.on_drop_brick(evt)
  }
  on_tap(i) {
    const name = this.component.brick_types[i].name
    if (this.main_handler.component.current_selection.length !== 0) {
      if (name === 'modify') {
        this.main_handler.edit_selection()
      } else if (name === 'cancel') {
        this.main_handler.cancel_selection()
      } else if (name === 'delete') {
        if (this.component.changes_disabled) {
          return
        }
        this.main_handler.delete_selection()
      }
    } else {
      if (this.component.changes_disabled) {
        return
      }
      this.main_handler.start_placing(name)
    }
  }
}

export class WorkflowInfoPanelHandler extends WorkflowComponentHandlerBase {}

export class WorkflowCanvasBrickGhostHandler extends WorkflowComponentHandlerBase {}

export class WorkflowCanvasConnectorGhostHandler extends WorkflowComponentHandlerBase {}

export class WorkflowMinimapHandler extends WorkflowComponentHandlerBase {
  constructor(component) {
    super(component)
    // this.on_drag_start = super.prevent_unallowed_workflow_modifications(
    //   this.on_drag_start.bind(this)
    // )
    // this.on_drag_move = super.prevent_unallowed_workflow_modifications(
    //   this.on_drag_move.bind(this)
    // )
    // this.on_drag_stop = super.prevent_unallowed_workflow_modifications(
    //   this.on_drag_stop.bind(this)
    // )
    this.on_drag_start = this.on_drag_start.bind(this)
    this.on_drag_move = this.on_drag_move.bind(this)
    this.on_drag_stop = this.on_drag_stop.bind(this)
  }
  do_start() {
    let interactable = interact(this.component.$refs.currentarea)
    this.interactable = interactable
    interactable
      .draggable({})
      .on('dragstart', this.on_drag_start)
      .on('dragmove', this.on_drag_move)
      .on('dragend', this.on_drag_stop)
      .styleCursor(false)
  }
  do_stop() {
    this.interactable
      .off('dragend', this.on_drag_end)
      .off('dragmove', this.on_drag_move)
      .off('dragstart', this.on_drag_start)
      .draggable(false)
    this.interactable.unset()
  }
  on_drag_start(evt) {
    this.start_mouse_position = {
      x: evt.clientX,
      y: evt.clientY,
    }
    this.start_element_position = this.main_handler.component.automl_workflow_pos
    this.position = this.start_element_position
  }
  position_from_event(evt) {
    let mouse_position = {
      x: evt.clientX,
      y: evt.clientY,
    }

    let dx = mouse_position.x - this.start_mouse_position.x
    let dy = mouse_position.y - this.start_mouse_position.y

    let { workflow_width, workflow_height } = {
      workflow_width: 3840,
      workflow_height: 2160,
    }
    let scale = this.main_handler.scale
    // let scale_factor = 448.0 / (3860 * scale)
    let horizontal_scale_factor =
      this.main_handler.component.elements_size.effective_map_width /
      (workflow_width * scale)
    let vertical_scale_factor =
      this.main_handler.component.elements_size.effective_map_height /
      (workflow_height * scale)

    let position = {
      x: this.start_element_position.x + dx / horizontal_scale_factor,
      y: this.start_element_position.y + dy / vertical_scale_factor,
    }

    // let scale = (this.main_handler.scale * 3860) / 448

    // let position = {
    //   x: this.start_element_position.x + dx * scale,
    //   y: this.start_element_position.y + dy * scale,
    // }
    return position
  }
  move_selection(pos) {
    this.main_handler.viewport.scrollLeft = pos.x
    this.main_handler.viewport.scrollTop = pos.y
  }
  on_drag_move(evt) {
    let pos = this.position_from_event(evt)
    this.move_selection(pos)
  }
  async on_drag_stop(evt) {
    let pos = this.position_from_event(evt)
    this.move_selection(pos)
  }
}

class WorkflowInterface {
  constructor(component) {
    this.component = component
    this.project_id = undefined
    this.started = false
    this.current_mode = undefined
    this.current_brick = undefined
    this.current_port = undefined
    this.handlers = {}
    this.brick_ghost_position = { x: 0, y: 0 }
    this.tooltip = undefined
    this.is_hovered = false
    this.show_grid = true
    this.show_descriptions = true
    this.drop_source = [-1, -1]
    this.drop_target = [-1, -1]
    this.drop_position = { x: 0, y: 0 }
    this.scale = 1
    this.drag_and_drop_is_active = false
    this.canvas_height = 0

    // this.on_mousewheel = prevent_unallowed_workflow_modifications(
    //   this.on_mousewheel.bind(this),
    //   this.component
    // )
    // this.on_mousemove_placing = prevent_unallowed_workflow_modifications(
    //   this.on_mousemove_placing.bind(this),
    //   this.component
    // )
    // this.on_drag_begin = prevent_unallowed_workflow_modifications(
    //   this.on_drag_begin.bind(this),
    //   this.component
    // )
    // this.on_drag_move = prevent_unallowed_workflow_modifications(
    //   this.on_drag_move.bind(this),
    //   this.component
    // )
    // this.on_drag_end = prevent_unallowed_workflow_modifications(
    //   this.on_drag_end.bind(this),
    //   this.component
    // )
    // this.cancel_selection = this.cancel_selection.bind(this)
    // this.delete_selection = prevent_unallowed_workflow_modifications(
    //   this.delete_selection.bind(this),
    //   this.component
    // )
    // this.on_tap_placing = prevent_unallowed_workflow_modifications(
    //   this.on_tap_placing.bind(this),
    //   this.component
    // )
    // this.on_tap_dragging = this.on_tap_dragging.bind(this)
    // this.on_mouseover = this.on_mouseover.bind(this)
    // this.on_mouseout = this.on_mouseout.bind(this)
    // this.on_drop_brick = prevent_unallowed_workflow_modifications(
    //   this.on_drop_brick.bind(this),
    //   this.component
    // )
    this.on_mousewheel = this.on_mousewheel.bind(this)
    this.on_mousemove_placing = this.on_mousemove_placing.bind(this)
    this.on_drag_begin = this.on_drag_begin.bind(this)
    this.on_drag_move = this.on_drag_move.bind(this)
    this.on_drag_end = this.on_drag_end.bind(this)
    this.cancel_selection = this.cancel_selection.bind(this)
    this.delete_selection = this.delete_selection.bind(this)
    this.on_tap_placing = this.on_tap_placing.bind(this)
    this.on_tap_dragging = this.on_tap_dragging.bind(this)
    this.on_mouseover = this.on_mouseover.bind(this)
    this.on_mouseout = this.on_mouseout.bind(this)
    this.on_drop_brick = this.on_drop_brick.bind(this)

    this.change_mode_dragging = () => this.change_mode('dragging')
  }

  install(subcomponent_handler) {
    const uid = subcomponent_handler.component.uid
    if (!(uid in this.handlers)) {
      this.handlers[uid] = subcomponent_handler
      subcomponent_handler.set_main_handler(this)
      if (this.started) {
        subcomponent_handler.start()
      }
    }
  }
  uninstall(subcomponent_handler) {
    const uid = subcomponent_handler.component.uid
    if (uid in this.handlers) {
      this.handlers[uid].stop()
      delete this.handlers[uid]
    }
  }
  select_all() {
    const uids = this.component.bricks_undeleted['lists']
    this.set_current_selection(uids)
  }
  set_current_selection(uids) {
    if (!_.isEmpty(uids) && _.isEmpty(this.component.current_selection)) {
      Mousetrap.bind('esc', this.cancel_selection)
      Mousetrap.bind('del', this.delete_selection)
    } else if (
      _.isEmpty(uids) &&
      !_.isEmpty(this.component.current_selection.length)
    ) {
      Mousetrap.unbind('del')
      Mousetrap.unbind('esc')
    }
    this.component.do_set_current_selection(uids)
  }
  cancel_selection() {
    this.set_current_selection([])
  }
  add_remove_to_current_selection(uids) {
    const res = _.xor(this.component.current_selection, uids)
    this.set_current_selection(res)
  }
  remove_from_current_selection(uids) {
    const res = _.difference(this.component.current_selection, uids)
    this.set_current_selection(res)
  }

  set_drop_source(d) {
    this.drop_source = d
  }
  set_drop_destination(d) {
    this.drop_target = d
  }
  async check_for_drop() {
    // Will not be called when changes_disabled, so no need to check
    if (this.drop_source[0] !== -1 && this.drop_target[0] !== -1) {
      const options = {
        from_brick_uid: this.drop_source[0],
        from_port: this.drop_source[1],
        to_brick_uid: this.drop_target[0],
        to_port: this.drop_target[1],
      }
      // Reverse the order if necessary
      // N.B PL : Isn't it redudant with the switch in the backend
      // in mid_create ?
      if (
        options.from_port.substr(-1) == 'l' &&
        options.to_port.substr(-1) == 'r'
      ) {
        let tmp = options.from_port
        options.from_port = options.to_port
        options.to_port = tmp

        tmp = options.from_brick_uid
        options.from_brick_uid = options.to_brick_uid
        options.to_brick_uid = tmp
      }
      await this.add_connector(options)
    }
    this.drop_source = [-1, -1]
    this.drop_target = [-1, -1]
  }

  change_mode(mode) {
    if (mode === this.current_mode) {
      return
    }
    if (this.current_mode === 'placing') {
      this.uninstall_handlers_for_placing()
    } else if (this.current_mode === 'dragging') {
      this.uninstall_handlers_for_dragging()
    } else if (this.current_mode === 'drag_and_dropping') {
      this.uninstall_handlers_for_drag_and_dropping()
    }
    if (mode === 'placing') {
      this.install_handlers_for_placing()
    } else if (mode === 'dragging') {
      this.install_handlers_for_dragging()
    } else if (mode === 'drag_and_dropping') {
      this.install_handlers_for_drag_and_dropping()
    }
    this.current_mode = mode
  }

  install_handlers_for_dragging() {
    this.interactable
      .draggable({
        mouseButtons: 3,
        autoScroll: {
          container: this.viewport,
        },
      })
      .on('dragstart', this.on_drag_begin)
      .on('dragmove', this.on_drag_move)
      .on('dragend', this.on_drag_end)
      .on('tap', this.on_tap_dragging)
      .styleCursor(false)
    Mousetrap.bind('ctrl+a', e => this.event_select_all(e))
  }
  uninstall_handlers_for_dragging() {
    Mousetrap.unbind('ctrl+a')
    this.interactable
      .off('tap', this.on_tap_dragging)
      .off('dragend', this.on_drag_end)
      .off('dragmove', this.on_drag_move)
      .off('dragstart', this.on_drag_begin)
      .draggable(false)
  }
  install_handlers_for_placing() {
    this.interactable
      .on('mousemove', this.on_mousemove_placing)
      .on('tap', this.on_tap_placing)
    Mousetrap.bind('esc', this.change_mode_dragging)
  }
  uninstall_handlers_for_placing() {
    Mousetrap.unbind('esc')
    this.interactable
      .off('tap', this.on_tap_placing)
      .off('mousemove', this.on_mousemove_placing)
  }
  install_handlers_for_drag_and_dropping() {
    this.interactable.dropzone({})
  }
  uninstall_handlers_for_drag_and_dropping() {
    this.interactable.dropzone(false)
  }
  start_placing(brick_name) {
    this.current_brick = brick_name
    this.change_mode('placing')
  }
  start_drag_and_dropping(brick_name) {
    this.current_brick = brick_name
    this.change_mode('drag_and_dropping')
  }
  async add_brick(brick_name, brick_options) {
    const brick_creator = brick_creator_from_name(brick_name)
    brick_options['brick_name'] = brick_name
    brick_options['project_id'] = this.project_id
    let brick_creation_options = {
      creator: brick_creator,
      members: brick_options,
    }
    this.change_mode('dragging')
    let res = await generic_create(this.project_id, brick_creation_options)
    return res
  }

  async add_connector(connector_options) {
    connector_options['project_id'] = this.project_id
    let connector_creation_options = {
      creator: 'automl_connector',
      members: connector_options,
    }
    this.change_mode('dragging')
    let res = undefined
    try {
      let res = await generic_create(
        this.project_id,
        connector_creation_options
      )
    } catch (err) {
      console.warn('Could not create connector')
    }
    return res
  }

  edit_selection() {
    if (this.component.current_selection.length === 1) {
      let uid = this.component.current_selection[0]
      this.edit_brick(uid)
    }
  }

  edit_brick(uid) {
    let project_id = this.project_id
    let brick_edition_path = `/projects/${project_id}/bricks/${uid}`
    this.component.$router.push(brick_edition_path)
  }

  delete_selection() {
    const uids = _.clone(this.component.current_selection)
    uids.map(uid => this.delete_object(uid))
  }
  object_from_uid(uid) {
    let res =
      this.component.bricks_undeleted.bricks[uid] ||
      this.component.connectors_undeleted.connectors[uid]
    return res
  }
  async delete_object(uid) {
    if (_.isUndefined(uid)) {
      return
    }
    this.remove_from_current_selection([uid])
    await generic_delete(this.project_id, { uid })
  }
  start() {
    if (this.started) {
      return
    }
    this.started = true

    let component = this.component
    let viewport = component.$refs['viewport']
    let panner = component.$refs['panner']
    this.viewport = viewport
    this.panner = panner

    this.viewport.style = 'overflow: scroll;width: 100%;'
    this.panner.style =
      'position: relative;overflow: hidden;transform-origin: 0 0;'

    this.viewport_listener = $(viewport)

    this.viewport_listener.on('wheel', this.on_mousewheel)

    this.panner.oncontextmenu = function() {
      return false
    }

    let interactable = interact(this.panner)
    this.interactable = interactable
    this.interactable
      .on('mouseover', this.on_mouseover)
      .on('mouseout', this.on_mouseout)

    this.change_mode('dragging')

    for (let uid in this.handlers) {
      this.handlers[uid].start()
    }

    // this.resize_control_panel()
  }
  stop() {
    if (!this.started) {
      return
    }
    this.started = false

    Object.values(this.handlers).forEach(v => v.stop())
    this.handlers = {}

    this.change_mode('none')
    $(this.viewport_listener).off()
  }

  event_select_all() {
    this.select_all()
    return false
  }

  on_drop_brick(evt) {
    if (this.is_hovered) {
      this.on_tap_placing(evt)
    }
  }

  set_scale(scale_index) {
    this.scale_index = scale_index
    this.scale = scale_from_scale_index(scale_index)
    let ps = this.panner.style
    ps.transform = `scale(${this.scale})`
    ps.width = `3840px`
    ps.height = `2160px`

    this.component.$store.commit('set_workflow_zoom_level', scale_index)
  }
  set_scrollpos(pos) {
    this.component.$refs.viewport.scrollLeft = pos.x
    this.component.$refs.viewport.scrollTop = pos.y
  }
  on_mousewheel(evt) {
    if (evt.ctrlKey) {
      evt.preventDefault()

      const bounds = this.panner.getBoundingClientRect()
      const mouse_position = {
        x: (evt.clientX - bounds.left) / this.scale,
        y: (evt.clientY - bounds.top) / this.scale,
      }

      let scale_index = this.scale_index
      if (evt.originalEvent.deltaY > 0) {
        scale_index -= 1
      } else {
        scale_index += 1
      }
      this.set_scale(scale_index)

      const scroll_offset = {
        x: mouse_position.x * this.scale + bounds.left - evt.clientX,
        y: mouse_position.y * this.scale + bounds.top - evt.clientY,
      }
      this.viewport.scrollLeft += scroll_offset.x
      this.viewport.scrollTop += scroll_offset.y
    }
  }
  on_drag_begin(evt) {
    if (evt.buttons & 2) {
      // Right mouse button
      this.drag_start = undefined
      this.drag_stop = undefined
    } else if (evt.buttons & 1) {
      const bounds = this.panner.getBoundingClientRect()
      let mouse_position = {
        x: (evt.clientX - bounds.left) / this.scale,
        y: (evt.clientY - bounds.top) / this.scale,
      }
      this.drag_start = mouse_position
      this.drag_stop = mouse_position
      this.component.area_selecting = true
    }
  }
  on_drag_move(evt) {
    if (evt.buttons & 2) {
      // Right mouse button
      this.viewport.scrollBy(-evt.dx, -evt.dy)
    } else if (evt.buttons & 1) {
      const bounds = this.panner.getBoundingClientRect()
      let mouse_position = {
        x: (evt.clientX - bounds.left) / this.scale,
        y: (evt.clientY - bounds.top) / this.scale,
      }
      this.drag_stop = mouse_position

      const [from, to] = [this.drag_start, this.drag_stop]
      const { x: startx, y: starty } = from
      const { x: stopx, y: stopy } = to
      const minx = Math.min(startx, stopx)
      const maxx = Math.max(startx, stopx)
      const miny = Math.min(starty, stopy)
      const maxy = Math.max(starty, stopy)
      const width = maxx - minx
      const height = maxy - miny

      let s = this.component.$refs.areaselector.style
      s.left = `${minx}px`
      s.top = `${miny}px`
      s.width = `${width}px`
      s.height = `${height}px`
    }
  }
  get_connector_segment(cuid) {
    let bs = this.component.bricks_undeleted.bricks

    const connector = this.component.connectors_undeleted.connectors[cuid]
    const lhs = bs[connector.from_brick_uid]
    const rhs = bs[connector.to_brick_uid]

    const { x: fromxb, y: fromyb } = lhs.position
    const { x: fromxc, y: fromyc } = brick_port_endpoint_positions[
      connector.from_port
    ]
    const { x: toxb, y: toyb } = rhs.position
    const { x: toxc, y: toyc } = brick_port_endpoint_positions[
      connector.to_port
    ]

    const from = point(fromxb + fromxc, fromyb + fromyc)
    const to = point(toxb + toxc, toyb + toyc)
    const seg = segment(from, to)
    return seg
  }
  on_drag_end(evt) {
    if (this.drag_start || this.drag_stop) {
      this.component.area_selecting = false

      const [from, to] = [this.drag_start, this.drag_stop]
      const { x: startx, y: starty } = from
      const { x: stopx, y: stopy } = to
      const minx = Math.min(startx, stopx)
      const maxx = Math.max(startx, stopx)
      const miny = Math.min(starty, stopy)
      const maxy = Math.max(starty, stopy)
      const box = new Flatten.Box(minx, miny, maxx, maxy)

      let bl = this.component.bricks_undeleted.lists
      let bs = this.component.bricks_undeleted.bricks
      const bricks_selected = bl.filter(uid => bs[uid].box.intersect(box))

      const poly = polygon_from_box(box)
      let cl = this.component.connectors_undeleted.lists
      let self = this
      let connector_segment_intersect = function(uid) {
        const seg = self.get_connector_segment(uid)
        const res =
          poly.box.intersect(seg.box) &&
          (!_.isEmpty(poly.intersect(seg)) ||
            seg.ps.box.intersect(box) ||
            seg.pe.box.intersect(box))
        return res
      }
      const connectors_selected = cl.filter(uid =>
        connector_segment_intersect(uid)
      )

      const selected = _.concat(bricks_selected, connectors_selected)

      if (evt.ctrlKey) {
        this.add_remove_to_current_selection(selected)
      } else {
        this.set_current_selection(selected)
      }
    }
  }
  on_mousemove_placing(evt) {
    if (this.component.changes_disabled) {
      return
    }
    const bounds = this.panner.getBoundingClientRect()
    let mouse_position = {
      x: (evt.clientX - bounds.left) / this.scale,
      y: (evt.clientY - bounds.top) / this.scale,
    }
    let dx = mouse_position.x
    let dy = mouse_position.y

    const offset = -workflow_brick_icon_size / 2 + workflow_grid_step / 2
    let position = {
      x: Math.floor((dx + offset) / workflow_grid_step) * workflow_grid_step,
      y: Math.floor((dy + offset) / workflow_grid_step) * workflow_grid_step,
    }

    this.brick_ghost_position = position
  }
  on_tap_placing() {
    if (this.component.changes_disabled) {
      return
    }
    this.add_brick(this.current_brick, {
      position: {
        x: this.brick_ghost_position.x + workflow_brick_icon_size / 2,
        y: this.brick_ghost_position.y + workflow_brick_icon_size / 2,
      },
    })
    this.cancel_selection()
  }
  on_tap_dragging() {
    this.cancel_selection()
  }
  on_mouseover() {
    this.is_hovered = true
  }
  on_mouseout() {
    this.is_hovered = false
  }
}

// *****************************************************************************

export default {
  name: 'WorkflowCore',
  components: {
    WorkflowCanvasBrick,
    WorkflowCanvasConnector,
    WorkflowCanvasBrickGhost,
    WorkflowCanvasConnectorGhost,
    WorkflowMinimap,
    WorkflowInfoPanel,
    WorkflowPalette,
    WorkflowPaletteDescription,
    GenericInAppScreen,
  },
  mixins: [subscription_mixin, brick_mixin],
  data() {
    return {
      workflow_interface: new WorkflowInterface(this),
      current_selection: [],
      area_selecting: false,
      canvas_size: { width: 0, height: 0 },
      window: $(window),
    }
  },
  metaInfo() {
    let project = this.automl_projects_undeleted_object.projects[
      this.project_id
    ]
    let project_name = (project || {}).name
    return {
      title: this.$t('project_x', { project_name }),
    }
  },

  computed: {
    project_id() {
      let route = this.$route.path
      let regexp = new RegExp('(\\d*)/workflow')
      return Number(route.match(regexp)[0].split('/')[0])
    },
    cur_project() {
      let res =
        (this.automl_projects_undeleted_object.projects || {})[
          this.project_id
        ] || {}
      return res
    },

    bricks_undeleted() {
      return this.automl_bricks_undeleted_object_per_project(this.project_id)
    },
    connectors_undeleted() {
      return this.automl_connectors_undeleted_object_per_project(
        this.project_id
      )
    },
    title() {
      let project = this.automl_projects_undeleted_object.projects[
        this.project_id
      ]
      let project_name = (project || {}).name
      return this.$t('project_x_workflow', { project_name })
    },
    panner_classes() {
      let classes = 'panner'.split(' ')
      if (this.workflow_interface.show_grid) {
        classes.push('grid')
      }
      return classes
    },
    tooltip_visible() {
      return !!this.workflow_interface.tooltip
    },
    selection_name() {
      let value = (
        this.workflow_interface.object_from_uid(this.current_selection) || {}
      ).name
      return value
    },
    component_options() {
      let res = {
        selected: this.current_selection,
      }
      return res
    },
    connector_options() {
      let res = {
        selected: this.current_selection,
      }
      return res
    },
    minimap_options() {
      let res = {}
      return res
    },
    info_panel_options() {
      let res = {
        position: this.current_selection,
      }
      return res
    },
    palette_options() {
      let res = {
        selected: this.current_selection,
      }
      return res
    },
    brick_ghost_options() {
      let res = {
        position: this.workflow_interface.brick_ghost_position,
      }
      return res
    },
    connector_ghost_options() {
      let res = {}
      return res
    },
    show_brick_ghost() {
      const res =
        (this.workflow_interface.current_mode === 'placing' ||
          this.workflow_interface.current_mode === 'drag_and_dropping') &&
        this.workflow_interface.is_hovered
      return res
    },
    show_connector_ghost() {
      const res = this.workflow_interface.current_mode === 'connecting'
      return res
    },
    palette_description_container_class() {
      let res = 'tooltip border'.split(' ')
      if (this.workflow_interface.tooltip) {
        if (
          this.changes_disabled &&
          this.workflow_interface.tooltip.name !== 'modify' &&
          this.workflow_interface.tooltip.name !== 'cancel'
        ) {
          res.push('bg-darkgrey border-lightgrey'.split(' '))
        } else {
          res.push('bg-darkblue border-lightblue'.split(' '))
        }
      }
      return res
    },
    ...mapState([
      'full_navbar_status',
      'full_navbar_target',
      'automl_workflow_zoom_level',
      'automl_workflow_pos',
      'window_size',
      'elements_size',
    ]),
    ...mapGetters([
      'automl_projects_undeleted_object',
      'automl_bricks_undeleted_object_per_project',
      'automl_connectors_undeleted_object_per_project',
    ]),
  },
  watch: {
    project_id: {
      handler(nv) {
        this.workflow_interface.project_id = nv
      },
      immediate: true,
    },
    // full_navbar_status() {
    //   this.workflow_interface.resize_control_panel()
    // },
    // full_navbar_target() {
    //   this.workflow_interface.resize_control_panel()
    // },
  },
  beforeMount() {
    this.bound_window_resizing_handler = this.window_resizing_handler.bind(this)
    this.fetch_collections([
      'bricks',
      'connectors',
      'projects',
      'rawfiles',
      'users',
      'project_rights',
    ])
  },
  mounted() {
    this.$store.commit('set_full_navbar_target', false)
    this.workflow_interface.start()
    // $(window).on('resize', this.workflow_interface.resize_control_panel)
    this.initial_populate()
    this.workflow_interface.set_scale(this.automl_workflow_zoom_level)
    this.workflow_interface.set_scrollpos(this.automl_workflow_pos)
    // this.$refs.viewport.on('resize', this.compute_canvas_size)
    // console.log($('workflow-minimap'))
    /*    const viewport = $('#viewport')
    console.log('viewport', viewport)
    console.log(viewport.width(), viewport.height())
    viewport.on('resize', () => this.compute_canvas_size())*/
    // console.log($('#viewport'))
    // $(window).on('resize', this.compute_canvas_size)
    // $(window).on('resize', this.actualize_window_size)

    this.window.on('resize', this.bound_window_resizing_handler)
    // let rect = this.$refs.viewport.getBoundingClientRect()
    // this.canvas_size = { width: rect.width, height: rect.height }
    // this.actualize_window_size()
    // We need to actualize the canvas size after
    // the minimap and palette have been created, which need
    // the window size to initialize themselves
    // this.$nextTick(() => {
    //   this.compute_canvas_size()
    // })
    this.bound_window_resizing_handler()
  },
  beforeDestroy() {
    this.workflow_interface.stop()
    this.$store.commit('set_full_navbar_target', true)
    // $(window).off('resize', this.workflow_interface.resize_control_panel)
    // $('#viewport').off('resize')
    this.window.off('resize', this.bound_window_resizing_handler)
  },
  methods: {
    compute_canvas_size() {
      let res = (this.$refs || {}).viewport
      if (!_.isUndefined(res)) {
        res = res.getBoundingClientRect()
      }

      this.canvas_size = { width: res.width, height: res.height }
      // console.log('updated canvas_size', this.canvas_size)
    },
    actualize_window_size() {
      this.$store.commit('set_window_size', {
        width: this.window.width(),
        height: this.window.height(),
      })
    },
    window_resizing_handler() {
      this.actualize_window_size()
      this.$nextTick(() => {
        this.compute_canvas_size()
      })
    },

    do_set_current_selection(uid) {
      this.current_selection = uid
    },

    async populate_simple_kaggle(nb_bricks_y) {
      let component_types = 'sourcefile sourcefile dispatch modelgen result finish'.split(
        ' '
      )

      let offset = Math.max(Math.floor((nb_bricks_y - 3) / 2), 0)

      let component_list = []
      let position_and_descriptions = [
        [
          {
            x: workflow_brick_size / 2,
            y: workflow_brick_size / 2 + (offset + 0) * workflow_brick_size,
          },
          // "Uploadez ici votre fichier d'entraînement",
          '',
        ],
        [
          {
            x: workflow_brick_size / 2,
            y: workflow_brick_size / 2 + (offset + 2) * workflow_brick_size,
          },
          // "Uploadez ici votre fichier d'évaluation",
          '',
        ],
        [
          {
            x: workflow_brick_size / 2 + 2 * workflow_brick_size,
            y: workflow_brick_size / 2 + (offset + 1) * workflow_brick_size,
          },
          // 'Configurez la brique du haut en entrainement et la brique du bas en évaluation',
          '',
        ],
        [
          {
            x: workflow_brick_size / 2 + 4 * workflow_brick_size,
            y: workflow_brick_size / 2 + (offset + 1) * workflow_brick_size,
          },
          // 'Configurez le modèle: colonne à prédire, colonne(s) à exclure et critère de score retenu',
          '',
        ],
        [
          {
            x: workflow_brick_size / 2 + 6 * workflow_brick_size,
            y: workflow_brick_size / 2 + (offset + 1) * workflow_brick_size,
          },
          // "Téléchargez le résultat de l'évaluation ici, n'oubliez pas de choisir les colonnes que vous souhaitez extraire",
          '',
        ],
        [
          {
            x: workflow_brick_size / 2 + 8 * workflow_brick_size,
            y: workflow_brick_size / 2 + (offset + 1) * workflow_brick_size,
          },
          // 'Brique de fin de workflow ne nécessitant aucune configuration',
          '',
        ],
      ]
      for (const [i, value] of component_types.entries()) {
        let [position, description] = position_and_descriptions[i]
        let brick = await this.workflow_interface.add_brick(value, {
          position,
          description,
        })
        component_list.push(brick.uid)
      }

      let connection_constants = []
      connection_constants.push({
        from_brick_uid: 0,
        from_port: 'mr',
        to_brick_uid: 2,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 1,
        from_port: 'mr',
        to_brick_uid: 2,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 2,
        from_port: 'mr',
        to_brick_uid: 3,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 3,
        from_port: 'mr',
        to_brick_uid: 4,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 4,
        from_port: 'mr',
        to_brick_uid: 5,
        to_port: 'ml',
      })
      for (const value of connection_constants) {
        value['from_brick_uid'] = component_list[value.from_brick_uid]
        value['to_brick_uid'] = component_list[value.to_brick_uid]
        await this.workflow_interface.add_connector(value)
      }
    },

    async populate_aggregation_kaggle(nb_bricks_y) {
      let offset = Math.max(Math.floor((nb_bricks_y - 9) / 2), 0)

      let component_types = (
        'sourcefile sourcefile sourcefile dispatch ' +
        'aggregation modelgen result finish'
      ).split(' ')

      let component_list = []
      let positions = [
        {
          x: workflow_brick_size / 2,
          y: workflow_brick_size / 2 + (offset + 0) * workflow_brick_size,
        },
        {
          x: workflow_brick_size / 2,
          y: workflow_brick_size / 2 + (offset + 2) * workflow_brick_size,
        },
        {
          x: workflow_brick_size / 2 + 2 * workflow_brick_size,
          y: workflow_brick_size / 2 + (offset + 3) * workflow_brick_size,
        },
        {
          x: workflow_brick_size / 2 + 2 * workflow_brick_size,
          y: workflow_brick_size / 2 + (offset + 1) * workflow_brick_size,
        },
        {
          x: workflow_brick_size / 2 + 4 * workflow_brick_size,
          y: workflow_brick_size / 2 + (offset + 2) * workflow_brick_size,
        },
        {
          x: workflow_brick_size / 2 + 6 * workflow_brick_size,
          y: workflow_brick_size / 2 + (offset + 2) * workflow_brick_size,
        },
        {
          x: workflow_brick_size / 2 + 8 * workflow_brick_size,
          y: workflow_brick_size / 2 + (offset + 2) * workflow_brick_size,
        },
        {
          x: workflow_brick_size / 2 + 10 * workflow_brick_size,
          y: workflow_brick_size / 2 + (offset + 2) * workflow_brick_size,
        },
      ]
      for (const [i, value] of component_types.entries()) {
        let position = positions[i]
        let brick = await this.workflow_interface.add_brick(value, { position })
        component_list.push(brick.uid)
      }

      let connection_constants = []
      connection_constants.push({
        from_brick_uid: 0,
        from_port: 'mr',
        to_brick_uid: 3,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 1,
        from_port: 'mr',
        to_brick_uid: 3,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 2,
        from_port: 'mr',
        to_brick_uid: 4,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 3,
        from_port: 'mr',
        to_brick_uid: 4,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 4,
        from_port: 'mr',
        to_brick_uid: 5,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 5,
        from_port: 'mr',
        to_brick_uid: 6,
        to_port: 'ml',
      })
      connection_constants.push({
        from_brick_uid: 6,
        from_port: 'mr',
        to_brick_uid: 7,
        to_port: 'ml',
      })
      for (const value of connection_constants) {
        value['from_brick_uid'] = component_list[value.from_brick_uid]
        value['to_brick_uid'] = component_list[value.to_brick_uid]
        await this.workflow_interface.add_connector(value)
      }
    },

    handle_scroll() {
      const pos = {
        x: this.$refs.viewport.scrollLeft,
        y: this.$refs.viewport.scrollTop,
      }
      this.$store.commit('set_workflow_pos', pos)
    },

    initial_populate() {
      const nb_bricks_y = Math.floor(
        this.workflow_interface.canvas_height / workflow_brick_size
      )
      const populate_type = this.$store.state.default_workflow_used
      let component = this.workflow_interface.component
      if (populate_type === 'simple_kaggle') {
        component.populate_simple_kaggle(nb_bricks_y)
      } else if (populate_type === 'aggregation_kaggle') {
        component.populate_aggregation_kaggle(nb_bricks_y)
      }
      this.$store.commit('use_default_workflow', 'null')
    },
  },
}
</script>
